import React, {
  useRef,
  createContext,
  useContext,
  useState,
  useEffect,
} from 'react';
import { authInstance, noAuthInstance } from '@app/service/axios';
import { v4 as uuidv4 } from 'uuid';
import userStore from '@app/stores/userStore';
import deviceStore from '@app/stores/deviceStore';
import {
  DEVICE_STORAGE_KEY,
  USER_STORAGE_KEY,
} from '@app/stores/storageKeys';
import settingStore from '@app/stores/settingStore';
import { useToast } from '@app/context/ToastContext';
import authService from '@app/service/authService';
import { isLooseEqual } from '@app/utils/equality';
import { isJwtExpired } from '@app/utils/jwt';


const XLangs = {
  ko: 'ko',
  en: 'en',
  jp: 'ja',
  cn: 'zh',
};

const debug = false;
const DUPLICATED_DEVICE_MESSAGE = 'Duplicated Device UUID';

function retryDuplicateDeviceRequest(originalRequest) {
  if (!originalRequest || originalRequest._duplicateDeviceRetry) return null;
  const requestData =
    typeof originalRequest.data === 'string'
      ? JSON.parse(originalRequest.data)
      : { ...originalRequest.data };
  requestData.uuid = uuidv4();
  originalRequest._duplicateDeviceRetry = true;
  originalRequest.data = JSON.stringify(requestData);
  return noAuthInstance(originalRequest);
}

function readPersistedState(storageKey) {
  try {
    const storedValue = localStorage.getItem(storageKey);
    return storedValue ? JSON.parse(storedValue) : null;
  } catch (error) {
    debug && console.warn(`Invalid local storage value: ${storageKey}`, error);
    return null;
  }
}

function isDeviceTokenLifecycleRequest(url = '') {
  return (
    url.startsWith('/api/devices/v1') ||
    url.startsWith('/api/auth/v1/devices')
  );
}

// 서명을 검증할 수 없는 토큰은 다시 사용하지 않도록 사용자와 디바이스 인증 저장값을 함께 제거합니다.
function clearPersistedAuthentication() {
  localStorage.removeItem(USER_STORAGE_KEY);
  localStorage.removeItem(DEVICE_STORAGE_KEY);
}

// AxiosContext 생성
const AxiosContext = createContext();

// AxiosProvider 컴포넌트
export const AxiosProvider = ({ children }) => {
  const { accessToken, setAccessToken, logout, saveLogin } = userStore();
  const { language } = settingStore();

  const { showToast } = useToast();
  const {
  fcmPushToken,
  deviceToken,
  addDevice,
  ensureDeviceToken,
  updateDevice
} = deviceStore();
  const [isAxiosReady, setIsAxiosReady] = useState(false);
  const isFirstReqRef = useRef(true);
  const refreshPromiseRef = useRef(null);
  // 인터셉터는 language 변경 때만 다시 등록하던 기존 수명주기를 유지하고 등록 시점 값을 고정합니다.
  const interceptorDependenciesRef = useRef(null);
  interceptorDependenciesRef.current = {
    accessToken,
    deviceToken,
    ensureDeviceToken,
    logout,
    saveLogin,
    setAccessToken,
    toast,
  };

  useEffect(() => {
    debug && console.log('axiosMount');
  }, []);

  useEffect(() => {
    if (!deviceToken || isJwtExpired(deviceToken)) {
      console.log('ensureDeviceToken');
      interceptorDependenciesRef.current.ensureDeviceToken();
    }
  }, [addDevice, deviceToken]);

  useEffect(() => {
    console.log('updateDevice');
    if (deviceToken && !isJwtExpired(deviceToken)) updateDevice();
  }, [deviceToken, fcmPushToken, updateDevice]);

  useEffect(() => {
    debug && console.log('useAxiosInstance mount');
    /* AUTH [REQUEST] */
    const authInstanceReq = authInstance?.interceptors?.request.use(
      function (config) {
        config.headers['Accept-Language'] = XLangs?.[language];
        // TODO 토큰 header에 추가
        const userInfo = readPersistedState(USER_STORAGE_KEY);
        let token = userInfo?.state?.accessToken;
        if (token) {
          // 헤더에 토큰 설정
          config.headers['access_token'] = token;
        } else return;
        return config;
      },
      function (error) {
        return Promise.reject(error);
      },
    );

    /* AUTH [RESPONSE] */
    const authInstanceRes = authInstance?.interceptors?.response.use(
      (response) => {
        const {
          accessToken: latestAccessToken,
          deviceToken: latestDeviceToken,
          ensureDeviceToken: latestEnsureDeviceToken,
          logout: latestLogout,
          saveLogin: latestSaveLogin,
          setAccessToken: latestSetAccessToken,
          toast: latestToast,
        } = interceptorDependenciesRef.current;
        debug && console.log('isFirstReqRef.current : ', isFirstReqRef.current);
        debug && console.log('saveLogin : ', latestSaveLogin);
        debug && console.log('deviceToken : ', latestDeviceToken);
        debug && console.log('accessToken : ', latestAccessToken);
        if (isFirstReqRef.current && !latestSaveLogin) {
          latestEnsureDeviceToken()
            .then((ensuredDeviceToken) => {
              if (!ensuredDeviceToken) return Promise.reject(new Error('Invalid device token'));
              return authService.refreshAuthUser({
                'user-token': latestAccessToken,
                'device-token': ensuredDeviceToken,
              });
            })
            .then((res) => {
              latestSetAccessToken(res.data.token);
            })
            .catch((e) => {
              latestLogout();
              return Promise.reject(e);
            });
          isFirstReqRef.current = false;
        }

        if (isLooseEqual(response?.data?.meta?.code, 208)) {
          if (response?.config?.useToast) latestToast(response);
        }
        return response;
      },
      async (error) => {
        // TODO 토큰 만료 또는 API 오류 처리
        const originalRequest = error.config;

        // 서명 오류 토큰은 만료 토큰과 달리 갱신 대상으로 신뢰할 수 없으므로 즉시 로그아웃합니다.
        // 이 분기를 저장값 파싱보다 먼저 실행해 저장소가 이미 비어 있어도 안전하게 로그인 화면으로 복귀시킵니다.
        if (
          error?.response?.status === 401 &&
          // CR-11 무효화 토큰(비밀번호 변경·잠금·역할 변경·탈퇴)도 갱신할 수 없으므로 함께 즉시 로그아웃한다.
          ['Signature err token', 'Revoked token'].includes(
            error?.response?.data?.meta?.systemMessage,
          )
        ) {
          interceptorDependenciesRef.current.logout();
          clearPersistedAuthentication();
          return Promise.reject(error);
        }

        const userInfo = readPersistedState(USER_STORAGE_KEY);
        const accessToken = userInfo?.state?.accessToken;

        debug && console.log('originalRequest : ', originalRequest);
        debug && console.log('isFirstReqRef.current : ', isFirstReqRef.current);
        const { saveLogin: latestSaveLogin } =
          interceptorDependenciesRef.current;
        debug && console.log('saveLogin : ', latestSaveLogin);
        if (isFirstReqRef.current && !latestSaveLogin) {
          if (
            error?.response?.status === 401 &&
            error?.response?.data?.meta?.systemMessage === 'Expired token'
          ) {
            interceptorDependenciesRef.current.logout();
          }
          isFirstReqRef.current = false;
          return;
        }

        if (
          error?.response?.status === 401 &&
          error?.response?.data?.meta?.systemMessage === 'Expired token' &&
          !originalRequest._retry &&
          accessToken != null
        ) {
          try {
            console.log('🔥 REFRESH TOKEN 🔥');
            originalRequest._retry = true;
            if (!refreshPromiseRef.current) {
              const refreshPromise = interceptorDependenciesRef.current
                .ensureDeviceToken()
                .then((ensuredDeviceToken) => {
                  if (!ensuredDeviceToken) throw error;
                  return authService.refreshAuthUser({
                    'user-token': accessToken,
                    'device-token': ensuredDeviceToken,
                  });
                })
                .then((tokenRes) => {
                  interceptorDependenciesRef.current.setAccessToken(
                    tokenRes.data.token,
                  );
                  return tokenRes;
                })
                .catch((refreshError) => {
                  interceptorDependenciesRef.current.logout();
                  throw refreshError;
                })
                .finally(() => {
                  if (refreshPromiseRef.current === refreshPromise) {
                    refreshPromiseRef.current = null;
                  }
                });
              refreshPromiseRef.current = refreshPromise;
            }
            await refreshPromiseRef.current;
            if (originalRequest.data instanceof FormData) {
              originalRequest.headers = {
                'Content-Type': 'multipart/form-data',
              };
            }
            return authInstance(originalRequest);
          } catch (e) {
            return Promise.reject(e);
          }
        } else {
          if (error?.config?.useToast) {
            interceptorDependenciesRef.current.toast(error?.response);
          }
          return Promise.reject(error);
        }
      },
    );

    /* NO-AUTH [REQUEST] */
    const noAuthInstanceReq = noAuthInstance?.interceptors?.request.use(
      function (config) {
        config.headers['Accept-Language'] = XLangs?.[language];
        const deviceInfo = JSON.parse(localStorage[DEVICE_STORAGE_KEY]);
        const token = deviceInfo?.state?.deviceToken;
        if (
          !isDeviceTokenLifecycleRequest(config.url) &&
          isJwtExpired(token)
        ) {
          return interceptorDependenciesRef.current
            .ensureDeviceToken()
            .then((ensuredDeviceToken) => {
            if (ensuredDeviceToken) {
              config.headers['access_token'] = ensuredDeviceToken;
            }
            return config;
          });
        }
        if (token) {
          config.headers['access_token'] = token;
        }
        return config;
      },
      function (error) {
        return Promise.reject(error);
      },
    );

    /* NO-AUTH [RESPONSE] */
    const noAuthInstanceRes = noAuthInstance?.interceptors?.response.use(
      (response) => {
        if (
          isLooseEqual(response?.data?.meta?.code, 208) &&
          isLooseEqual(response?.data?.meta?.systemMessage, DUPLICATED_DEVICE_MESSAGE) &&
          isLooseEqual(response?.data?.meta?.userMessage,
            '이미 등록 된 디바이스 아이디입니다.')
        ) {
          const retry = retryDuplicateDeviceRequest(response.config);
          if (retry) return retry;
        }
        isFirstReqRef.current = false;
        return response;
      },
      (error) => {
        if (
          error?.response?.status === 409 &&
          error?.response?.data?.meta?.systemMessage ===
            DUPLICATED_DEVICE_MESSAGE
        ) {
          const retry = retryDuplicateDeviceRequest(error.config);
          if (retry) return retry;
        }
        if (error?.config?.useToast) {
          interceptorDependenciesRef.current.toast(error?.response);
        }
        return Promise.reject(error);
      },
    );

    setIsAxiosReady(true);

    return () => {
      authInstance?.interceptors?.request.eject(authInstanceReq);
      authInstance?.interceptors?.response.eject(authInstanceRes);
      noAuthInstance?.interceptors?.request.eject(noAuthInstanceReq);
      noAuthInstance?.interceptors?.response.eject(noAuthInstanceRes);
    };
  }, [language]);

  function toast(res) {
    debug && console.log('res : ', res);

    const code = res?.data?.meta?.code;
    const userMessage = res?.data?.meta?.userMessage;
    const systemMessage = res?.data?.meta?.systemMessage;
    debug && console.log('🔥 Axios Toast 🔥');
    debug && console.log(code);
    debug && console.log(userMessage);
    debug && console.log(userMessage);
    if (code > 200) {
      if (userMessage) {
        showToast(userMessage);
      } else if (systemMessage) {
        showToast(systemMessage);
      }
    } else if (code > 399 && code < 500) {
      if (userMessage) {
        showToast(userMessage);
      } else if (systemMessage) {
        showToast(systemMessage);
      }
    } else {
      if (userMessage) {
        showToast(userMessage);
      } else if (systemMessage) {
        showToast(systemMessage);
      }
    }
  }

  return (
    <AxiosContext.Provider value={{ isAxiosReady }}>
      {isAxiosReady && <>{children}</>}
    </AxiosContext.Provider>
  );
};

// useToast 훅
export const useAxios = () => {
  const context = useContext(AxiosContext);
  if (!context) {
    throw new Error('useAxios must be used within a AixosProvider');
  }
  return context;
};
